// EN-1196 merge visits client. Every call goes through apiFetch (impersonation + clinic headers).
import * as Sentry from "@sentry/react";
import { apiFetch } from "../api";
import { isMergeBlockReason, type MergeBlockReason } from "@/features/merge-visits/copy";

export type { MergeBlockReason };
export const OVERRIDE_FIELDS = ["scheduled_at", "appointment_duration", "type", "resource_id", "doctor_id"] as const;
export type OverrideField = (typeof OVERRIDE_FIELDS)[number];
export type FieldOverrides = Partial<Record<OverrideField, boolean>>;

export type MergeState = {
    /** Set when this visit was merged away: the visit it now lives in. Absent on an older API. */
    mergedInto?: { keptVisitId: string } | null;
    duplicate: {
        otherVisitId: string;
        otherLabel: string;
        otherLinked: boolean;
        suggestedTargetId: string;
        /** What is on the other visit. Absent on an older API. */
        otherDoctorId?: string | null;
        otherClipCount?: number;
        otherDocCount?: number;
    } | null;
    latestMerge: {
        mergeId: string;
        sourceId: string;
        sourceLabel: string;
        mergedByEmail: string | null;
        mergedAt: string;
        unmergeUntil: string;
        canUnmerge: boolean;
        moved: { clips: number; documents: number; files: number; calls: number };
        /** transcript_section, chat_completion and document_asset ids that moved in (for "Merged in" tags). */
        movedIds: string[];
        /** Headers of moved documents exported after the merge (shown in the unmerge confirm). */
        exportedSinceMerge: string[];
    } | null;
};
export type VisitSummary = {
    id: string;
    patientName: string | null;
    scheduledAt: string | null;
    createdAt: string;
    linked: boolean;
    clipCount: number;
    docCount: number;
};
export type VisitDetails = VisitSummary & {
    appointmentDuration: number | null;
    type: string | null;
    resourceId: string | null;
    doctorId: string | null;
    /** The visit has its own room rows, so the server refuses a Room override onto it. Absent on an older API. */
    hasOwnRooms?: boolean;
};
export type CandidateRow = {
    visit: VisitSummary;
    group: "likely" | "recent";
    eligible: boolean;
    reason: MergeBlockReason | null;
};
export type Manifest = {
    clips: { id: string; name: string; recordedAt: string | null; durationSeconds: number | null }[];
    documents: { id: string; header: string; createdAt: string; exportedTo: string | null }[];
    files: { id: string; name: string }[];
    calls: { id: string; createdAt: string }[];
};
export type MergePreview = {
    suggestion: { targetId: string; reason: "linked" | "content" | "current" };
    visits: Record<string, VisitDetails>;
    manifests: Record<string, Manifest>;
    sameTypeDocuments: string[];
    /** Per choice of kept visit: whether keeping that visit leaves a linked visit. */
    resultLinked: Record<string, boolean>;
};
/** A 500 code: the merge or unmerge definitely did not commit. A 500 without it may have. */
export const MERGE_FAILED = "MERGE_FAILED";

export type MergeResult =
    /** audioUnconfirmed: clips whose audio the server could not confirm at their new home; present only when above zero. */
    | { ok: true; mergeId: string; audioUnconfirmed?: number }
    /** reason null: the server sent a reason this build does not know. */
    | { ok: false; kind: "blocked"; reason: MergeBlockReason | null }
    /** Nothing changed. retryable false: the same request will fail the same way; reason is the server's. */
    | { ok: false; kind: "audio"; retryable: boolean; reason?: string }
    /**
     * status: the HTTP status, absent for a 2xx with no merge id. Every 4xx comes before the RPC, so it never
     * committed. code: the server's error code, e.g. OVERRIDES_NOT_ALLOWED (the kept visit became linked, or has its own room),
     * or MERGE_FAILED on a 500 that definitely did not commit.
     */
    | { ok: false; kind: "error"; status?: number; code?: string }
    /**
     * 503 MERGE_BUSY: the visits were busy (a database lock or timeout, rolled back) or the visit lock could not
     * be taken (e.g. Redis down). Nothing changed; try again.
     */
    | { ok: false; kind: "busy" }
    /**
     * No HTTP response (timeout or network error), or a 502/503/504 that is not ours (not AUDIO_COPY_FAILED or
     * MERGE_BUSY): the request may still have committed. Poll fetchMergeStatus (merge) or fetchMergeStatusById (unmerge).
     */
    | { ok: false; kind: "unknown" };
/** audioUnconfirmed: as on a 200 from merge or unmerge; present only when above zero. */
export type MergeStatus = { mergeId: string; status: "merged" | "unmerged"; audioUnconfirmed?: number };

const base = "v1/patient-records";
// The merge request copies audio synchronously, so it can outlive the api client's 30 s default.
const MERGE_TIMEOUT_MS = 120_000;

const statusOf = (error: unknown): number | null =>
    (error as { response?: { status?: number } })?.response?.status ?? null;
const bodyOf = async (error: unknown): Promise<{ code?: string; reason?: unknown; retryable?: unknown } | null> => {
    try {
        return await (error as { response: Response }).response.clone().json();
    } catch {
        return null;
    }
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// The server rejects merge-state requests with more than 200 ids.
export const MERGE_STATE_BATCH = 200;

/** null on a 404 (not deployed). Any other failure throws, so React Query keeps the last good data. */
async function fetchMergeStateBatch(ids: string[]): Promise<Record<string, MergeState> | null> {
    try {
        // apiFetch already reports unexpected failures to Sentry. A 404 is the dormant signal, so it is not reported.
        return (
            (await apiFetch<Record<string, MergeState>>(`${base}/merge-state`, {
                method: "POST",
                json: { ids },
                skipSentryOn404: true,
                skipSentryOnStatuses: [409],
            })) ?? {}
        );
    } catch (error) {
        if (statusOf(error) === 404) return null;
        throw error;
    }
}

/**
 * Ids are de-duplicated, non-UUIDs dropped, and the rest sent in batches of 200 in parallel.
 * null means the endpoint 404s (any batch): the feature is not deployed, so the UI stays dormant.
 * Batches that failed otherwise are left out (their visits show no merge state until the next poll);
 * the call rejects only when every batch failed.
 */
export const fetchMergeStates = async (ids: string[]): Promise<Record<string, MergeState> | null> => {
    const valid = Array.from(new Set(ids)).filter((id) => UUID_RE.test(id));
    if (valid.length === 0) return {};
    const batches: string[][] = [];
    for (let i = 0; i < valid.length; i += MERGE_STATE_BATCH) batches.push(valid.slice(i, i + MERGE_STATE_BATCH));
    const settled = await Promise.allSettled(batches.map(fetchMergeStateBatch));
    const answered = settled.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
    if (answered.some((r) => r === null)) return null;
    if (answered.length === 0) throw (settled[0] as PromiseRejectedResult).reason;
    return Object.assign({}, ...answered) as Record<string, MergeState>;
};

/**
 * selfBlock: set when the opened visit itself can't be merged right now (e.g. it is being recorded on);
 * reason null is a reason this build does not know. null when it can, or from an older API that does not say.
 */
export type MergeCandidates = { rows: CandidateRow[]; selfBlock: { reason: MergeBlockReason | null } | null };

export const fetchMergeCandidates = async (visitId: string, q: string | null): Promise<MergeCandidates> => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    const r = await apiFetch<{ rows: CandidateRow[]; self?: { reason?: unknown } }>(
        `${base}/${visitId}/merge-candidates${qs}`,
        { skipSentryOn404: true },
    );
    const reason = r?.self?.reason;
    return {
        rows: r?.rows ?? [],
        selfBlock: reason == null ? null : { reason: isMergeBlockReason(reason) ? reason : null },
    };
};

export const fetchMergePreview = async (visitId: string, otherId: string): Promise<MergePreview> => {
    const r = await apiFetch<MergePreview>(
        `${base}/merge-preview?visitId=${encodeURIComponent(visitId)}&otherId=${encodeURIComponent(otherId)}`,
        { skipSentryOn404: true },
    );
    if (!r) throw new Error("Empty merge preview");
    return r;
};

// apiFetch reports every failure except 400, 409, 502 and 503. A 400, 502 or 503 is reported here, once,
// with the server's code and reason, which apiFetch's event lacks. Expected answers are not reported: a 503
// MERGE_BUSY, and a 400 OVERRIDES_NOT_ALLOWED (the preview went stale: the kept visit was linked or got rooms).
type MergeOk = { mergeId: string; audioUnconfirmed?: number };

async function toResult(endpoint: string, call: () => Promise<MergeOk | undefined>): Promise<MergeResult> {
    try {
        const r = await call();
        if (typeof r?.mergeId !== "string") return { ok: false, kind: "error" };
        const n = r.audioUnconfirmed;
        return typeof n === "number" && n > 0
            ? { ok: true, mergeId: r.mergeId, audioUnconfirmed: n }
            : { ok: true, mergeId: r.mergeId };
    } catch (error) {
        const status = statusOf(error);
        if (status === 409) {
            const body = await bodyOf(error);
            if (body?.reason) {
                return { ok: false, kind: "blocked", reason: isMergeBlockReason(body.reason) ? body.reason : null };
            }
        }
        if (status === 502 || status === 503 || status === 504) {
            // Only our own AUDIO_COPY_FAILED body proves the audio copy failed. A bare gateway error (e.g. Kong timing out)
            // says nothing about whether the merge committed.
            const body = await bodyOf(error);
            if (status === 503 && body?.code === "MERGE_BUSY") return { ok: false, kind: "busy" };
            if (status === 502 || status === 503) {
                Sentry.captureException(error, {
                    tags: {
                        errorType: "api_fetch_error",
                        errorCategory: body?.code === "AUDIO_COPY_FAILED" ? "code_bug" : "connectivity",
                        endpoint,
                    },
                    extra: { code: body?.code, retryable: body?.retryable, reason: body?.reason },
                });
            }
            if (body?.code !== "AUDIO_COPY_FAILED") return { ok: false, kind: "unknown" };
            return {
                ok: false,
                kind: "audio",
                retryable: body.retryable !== false,
                ...(typeof body.reason === "string" ? { reason: body.reason } : {}),
            };
        }
        if (status === null) return { ok: false, kind: "unknown" };
        if (status === 400) {
            const body = await bodyOf(error);
            if (body?.code !== "OVERRIDES_NOT_ALLOWED") {
                Sentry.captureException(error, {
                    tags: { errorType: "api_fetch_error", errorCategory: "code_bug", endpoint },
                    extra: { code: body?.code, reason: body?.reason },
                });
            }
            if (typeof body?.code === "string") return { ok: false, kind: "error", status, code: body.code };
        }
        if (status >= 500) {
            // A bare 500 means the outcome is unknown; MERGE_FAILED means it definitely did not commit.
            const body = await bodyOf(error);
            if (typeof body?.code === "string") return { ok: false, kind: "error", status, code: body.code };
        }
        return { ok: false, kind: "error", status };
    }
}

export const postMerge = async (body: {
    sourceId: string;
    targetId: string;
    overrides: FieldOverrides;
    idempotencyKey: string;
}): Promise<MergeResult> => {
    const r = await toResult(`${base}/merge`, () =>
        apiFetch<MergeOk>(`${base}/merge`, {
            method: "POST",
            json: body,
            timeout: MERGE_TIMEOUT_MS,
            skipSentryOnStatuses: [400, 409, 502, 503],
        }),
    );
    // A 2xx without a merge id: apiFetch saw success, so nothing else reports it. The unmerge callers
    // report their own failures.
    if (!r.ok && r.kind === "error" && r.status === undefined) {
        Sentry.captureException(new Error("Merge answered without a merge id"), {
            tags: { errorType: "visit_merge", errorCategory: "code_bug", endpoint: `${base}/merge` },
        });
    }
    return r;
};

export const postUnmerge = (mergeId: string) => {
    const path = `${base}/merges/${encodeURIComponent(mergeId)}/unmerge`;
    return toResult(path, () =>
        apiFetch<MergeOk>(path, {
            method: "POST",
            timeout: MERGE_TIMEOUT_MS,
            skipSentryOnStatuses: [400, 409, 502, 503],
        }),
    );
};

async function fetchStatus(by: "idempotencyKey" | "mergeId", value: string): Promise<MergeStatus | null> {
    try {
        const r = await apiFetch<MergeStatus>(`${base}/merges/status?${by}=${encodeURIComponent(value)}`, {
            skipSentryOn404: true,
        });
        if (!r) return null;
        const n = r.audioUnconfirmed;
        return typeof n === "number" && n > 0
            ? { mergeId: r.mergeId, status: r.status, audioUnconfirmed: n }
            : { mergeId: r.mergeId, status: r.status };
    } catch (error) {
        if (statusOf(error) === 404) return null;
        throw error;
    }
}

/** Resolves a merge by idempotency key (null when none exists yet). Safe to poll after a timeout. */
export const fetchMergeStatus = (idempotencyKey: string) => fetchStatus("idempotencyKey", idempotencyKey);

/** Resolves a merge by id: after an unmerge with no answer, "unmerged" means it worked. */
export const fetchMergeStatusById = (mergeId: string) => fetchStatus("mergeId", mergeId);

export const dismissPair = async (a: string, b: string): Promise<void> => {
    await apiFetch(`${base}/duplicates/dismiss`, { method: "POST", json: { visitIds: [a, b] } });
};
export const undismissPair = async (a: string, b: string): Promise<void> => {
    await apiFetch(`${base}/duplicates/dismiss`, { method: "DELETE", json: { visitIds: [a, b] } });
};
